// store/slices/usersSlice.ts
import { Courses, University } from "@/store/types/aiCourseFinder";
import { CourseItem } from "@/store/types/courseTypes";
import { UniversityItem } from "@/store/types/universityTypes";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import {
  getEligibilityCoursesResults,
  getEligibilityDetails,
  getEligibilityUniversity,
  getFavoritesCards,
  getGlobalSearchFilter,
  getInstitutesResults,
  getProgramsResults,
  saveEligibilityDetails,
  submitCountryQuiz,
} from "./aiCourseApi";
import {
  CountryQuizAnswer,
  CountryQuizResult,
} from "@/store/types/aiCourseFinder";

interface IntakeDetails {
  month: string[];
  status: string[];
  deadline: string[];
}

interface CourseFinderFiltersState {
  intake: string;
  stream: string;
  duration: string;
}

interface AiCourseFinderEligibilityDetails {
  country: string;
  loading: boolean;
  globalSearch: string;
  academicDetails: {
    selectStream: string | number;
    pastQualification: string;
    stream: string;
    passingYear: string;
    gradingSystem: string;
    pastMathsGrades: string;
    pastEnglishGrades: string;
    pastChemistryGrades: string;
    pastPhysicsGrades: string;
    pastGrades: string;
    selectBoard: string;
    board: string;
  };
  englishProficiencyDetails: {
    test: string;
    listeningScore: string;
    readingScore: string;
    writingScore: string;
    speakingScore: string;
    englishMarks: string;
    overallScore: string;
    selectBoard: string;
  };
  programDetails: {
    qualificationLevel: string;
    stream: string;
    specialization: string;
    intake: string;
  };
  instituteDetails: {
    category: string;
    intake: string;
  };
  institute: University[];
  program: Courses[];
  eligibilityUniversities: University[];
  eligibilityCourses: Courses[];
  universityTotal: number;
  courseTotal: number;
  searchPageResults: {
    searchList: any;
    suggestionList: any;
    apiMessage: string;
    apiOneLiner: string;
    lastSearchLimit: number;
    lastSearchQuery: string;
  };
  courseFinderFilters: CourseFinderFiltersState;
  globalSearchCourse: Courses[];

  universityComparisonData: UniversityItem[];
  courseComparisonData: CourseItem[];
  sayItData: Courses[];
  courseFilterOptions: Array<{ [key: string]: string }>;
  courseSpecialisationOptions: Array<{ [key: string]: string }>;
  courseDuration: Array<{ [key: string]: string }>;
  courseStream: Array<{ [key: string]: string }>;
  specialisationCategory: string[];
  intakeOptions: IntakeDetails[];
  courseNames: Array<{ [key: string]: string }>;

  currentDetail: {
    countryId: string;
    collegeId: string;
    collegeName: string;
    countryName: string;
  };
  favoritesCardList: {
    colleges: University[];
    courses: Courses[];
  };
  topPickCard: CourseItem | null;
  lastSearchTop3Courses: any[];
  savedEligibility: { [key: string]: string | number };
  countryQuiz: {
    answers: CountryQuizAnswer[];
    result: CountryQuizResult | null;
    loading: boolean;
  };
}

const initialState: AiCourseFinderEligibilityDetails = {
  loading: false,
  country: "",
  globalSearch: "",
  academicDetails: {
    pastQualification: "",
    pastMathsGrades: "",
    pastEnglishGrades: "",
    pastChemistryGrades: "",
    pastPhysicsGrades: "",
    stream: "",
    passingYear: "",
    gradingSystem: "",
    pastGrades: "",
    selectBoard: "",
    board: "",
    selectStream: ""
  },
  englishProficiencyDetails: {
    test: "",
    listeningScore: "",
    readingScore: "",
    writingScore: "",
    speakingScore: "",
    overallScore: "",
    englishMarks: "",
    selectBoard: "",
  },
  programDetails: {
    qualificationLevel: "",
    stream: "",
    specialization: "",
    intake: "",
  },
  instituteDetails: {
    category: "",
    intake: "",
  },
  institute: [],
  program: [],
  eligibilityUniversities: [],
  eligibilityCourses: [],
  universityTotal: 0,
  courseTotal: 0,
  universityComparisonData: [],
  courseComparisonData: [],
  courseFilterOptions: [],
  courseSpecialisationOptions: [],
  courseDuration: [],
  courseStream: [],
  specialisationCategory: [],
  sayItData: [],
  currentDetail: {
    collegeId: "",
    countryId: "",
    collegeName: "",
    countryName: "",
  },
  favoritesCardList: {
    colleges: [],
    courses: [],
  },
  topPickCard: null,
  lastSearchTop3Courses: [],
  globalSearchCourse: [],
  savedEligibility: {},
  countryQuiz: {
    answers: [],
    result: null,
    loading: false,
  },
  intakeOptions: [],
  courseNames: [],
  searchPageResults: {
    searchList: null,
    suggestionList: null,
    apiMessage: "",
    apiOneLiner: "",
    lastSearchLimit: 0,
    lastSearchQuery: "",
  },
  courseFinderFilters: {
    intake: "All",
    stream: "All",
    duration: "All",
  },
};

const ensureAcademicDetailsState = (
  state: AiCourseFinderEligibilityDetails,
) => {
  if (!state.academicDetails) {
    state.academicDetails = { ...initialState.academicDetails };
  }

  return state.academicDetails;
};

const ensureEnglishProficiencyDetailsState = (
  state: AiCourseFinderEligibilityDetails,
) => {
  if (!state.englishProficiencyDetails) {
    state.englishProficiencyDetails = {
      ...initialState.englishProficiencyDetails,
    };
  }

  return state.englishProficiencyDetails;
};

const aiCourseFinderSlice = createSlice({
  name: "aiCourseFinder",
  initialState,
  reducers: {
    setAiGlobalSearch: (state, action) => {
      state.globalSearch =
        typeof action.payload === "string" ? action.payload : "";
    },

    setTopPickCard: (state, action) => {
      state.topPickCard = action.payload;
      if (typeof window !== "undefined") {
        try {
          if (action.payload) {
            localStorage.setItem("courseFinderFirstCard", JSON.stringify(action.payload));
          } else {
            localStorage.removeItem("courseFinderFirstCard");
          }
        } catch { }
      }
    },
    setAiCourseFinderCountry: (state, action) => {
      state.country = action.payload;
      if (typeof window !== "undefined") {
        try {
          localStorage.setItem("courseFinderSelectedCountry", String(action.payload || ""));
        } catch { }
      }
    },
    setLastSearchTop3Courses: (state, action) => {
      state.lastSearchTop3Courses = action.payload || [];
    },
    setAiCourseFinderAcademicDetails: (state, action) => {
      const payload = action.payload || {};
      const academicDetails = ensureAcademicDetailsState(state);
      const englishProficiencyDetails =
        ensureEnglishProficiencyDetailsState(state);

      state.academicDetails = {
        ...initialState.academicDetails,
        ...academicDetails,
        ...payload,
      };

      if (payload.pastEnglishGrades !== undefined) {
        englishProficiencyDetails.englishMarks = payload.pastEnglishGrades;
      }

      if (typeof window !== "undefined") {
        try {
          localStorage.setItem("courseFinderAcademicDetails", JSON.stringify(state.academicDetails));
        } catch { }
      }
    },
    setAiCourseFinderEnglishProficiencyDetails: (state, action) => {
      const payload = action.payload || {};
      const academicDetails = ensureAcademicDetailsState(state);
      const englishProficiencyDetails =
        ensureEnglishProficiencyDetailsState(state);

      state.englishProficiencyDetails = {
        ...initialState.englishProficiencyDetails,
        ...englishProficiencyDetails,
        ...payload,
      };

      if (payload.englishMarks !== undefined) {
        academicDetails.pastEnglishGrades = payload.englishMarks;
      }

      if (typeof window !== "undefined") {
        try {
          localStorage.setItem("courseFinderEnglishProficiencyDetails", JSON.stringify(state.englishProficiencyDetails));
        } catch { }
      }
    },
    restoreAiCourseFinderState: (state) => {
      if (typeof window === "undefined") return;
      try {
        const savedAcad = localStorage.getItem("courseFinderAcademicDetails");
        if (savedAcad) {
          state.academicDetails = {
            ...ensureAcademicDetailsState(state),
            ...JSON.parse(savedAcad),
          };
        }
        const savedEng = localStorage.getItem("courseFinderEnglishProficiencyDetails");
        if (savedEng) {
          state.englishProficiencyDetails = {
            ...ensureEnglishProficiencyDetailsState(state),
            ...JSON.parse(savedEng),
          };
        }
        const savedCountry = localStorage.getItem("courseFinderSelectedCountry");
        if (savedCountry && !state.country) {
          state.country = savedCountry;
        }
        const savedTopPick = localStorage.getItem("courseFinderFirstCard");
        if (savedTopPick && !state.topPickCard) {
          state.topPickCard = JSON.parse(savedTopPick);
        }
      } catch (err) {
        console.error("Failed to restore course finder state from localStorage", err);
      }
    },
    resetAiCourseFinderEnglishProficiencyDetails: (state) => {
      state.englishProficiencyDetails = initialState.englishProficiencyDetails;
    },

    resetAiCourseFinderAcademicDetails: (state) => {
      state.academicDetails = initialState.academicDetails;
    },

    resetAiCourseFinderProgramDetails: (state) => {
      state.programDetails = initialState.programDetails;
    },

    resetAiCourseFinderInstituteDetails: (state) => {
      state.instituteDetails = initialState.instituteDetails;
    },

    setAiCourseFinderProgramDetails: (state, action) => {
      state.programDetails = action.payload;
    },
    setAiCourseFinderInstituteDetails: (state, action) => {
      state.instituteDetails = action.payload;
    },

    setCurrentDetail: (state, action) => {
      state.currentDetail = {
        ...state.currentDetail,
        collegeId: action.payload.collegeId,
        countryId: action.payload.countryId,
        collegeName: action.payload.collegeName,
        countryName: action.payload.countryName,
      };
    },

    saveEligibilityAnonymous: (state, action) => {
      state.savedEligibility = {
        ...action.payload,
        countryId: action.payload?.id || action.payload?.countryId,
      };
    },

    setCountryQuizAnswer: (state, action: PayloadAction<CountryQuizAnswer>) => {
      const nextAnswer = action.payload;

      if (!state.countryQuiz) {
        state.countryQuiz = {
          answers: [],
          result: null,
          loading: false,
        };
      }

      const existingIndex = state.countryQuiz.answers.findIndex(
        (item) => item.step === nextAnswer.step,
      );

      if (existingIndex >= 0) {
        state.countryQuiz.answers[existingIndex] = nextAnswer;
      } else {
        state.countryQuiz.answers.push(nextAnswer);
      }
    },

    setCountryQuizResult: (state, action: PayloadAction<CountryQuizResult>) => {
      state.countryQuiz.result = action.payload;
    },

    setCountryQuizLoading: (state, action: PayloadAction<boolean>) => {
      state.countryQuiz.loading = action.payload;
    },

    clearCountryQuiz: (state) => {
      state.countryQuiz = initialState.countryQuiz;
    },

    setGlobalSearchCourses: (state, action) => {
      state.globalSearchCourse = action.payload;
    },

    setUniversityComparisonData: (state, action) => {
      state.universityComparisonData = action.payload;
    },

    setCourseComparisonData: (state, action) => {
      state.courseComparisonData = action.payload;
    },



    setGlobalSearchResults: (state, action) => {
      state.searchPageResults = {
        ...state.searchPageResults,
        ...action.payload,
      };
    },
    setCourseFinderFilters: (
      state,
      action: PayloadAction<Partial<CourseFinderFiltersState>>,
    ) => {
      state.courseFinderFilters = {
        ...state.courseFinderFilters,
        ...action.payload,
      };
    },

    setEligibilityCoursesResults: (state, action) => {
      state.eligibilityCourses = action.payload.data;
      state.courseTotal = action.payload.total_count;
    },
    setEligibilityUniversityResults: (state, action) => {
      state.eligibilityUniversities = action.payload.list;
      state.universityTotal = action.payload.total_count;
    },

    clearAiCourseFinder: () => initialState,
  },
  extraReducers: (builder) => {
    builder
      // GET INSTITUTE RESULTS
      .addCase(getInstitutesResults.pending, (state) => {
        state.institute = [];
        state.universityTotal = 0;
      })
      .addCase(getInstitutesResults.fulfilled, (state, action) => {
        state.institute = action.payload.list;
        state.universityTotal = action.payload.total;
      })
      .addCase(getInstitutesResults.rejected, (state) => {
        state.institute = [];
        state.universityTotal = 0;
      })

      // GET PROGRAM RESULTS
      .addCase(getProgramsResults.pending, (state) => {
        state.program = [];
        state.courseTotal = 0;
      })
      .addCase(getProgramsResults.fulfilled, (state, action) => {
        state.program = action.payload.list;
        state.courseTotal = action.payload.total;
      })
      .addCase(getProgramsResults.rejected, (state) => {
        state.program = [];
        state.courseTotal = 0;
      })

      // GET ELIGIBILITY RESULTS
      .addCase(getEligibilityCoursesResults.pending, (state) => {
        state.loading = true;
        state.eligibilityCourses = [];
        state.courseTotal = 0;
      })
      .addCase(getEligibilityCoursesResults.fulfilled, (state, action) => {
        state.loading = false;
        state.eligibilityCourses = action.payload.list;
        state.courseTotal = action.payload.total;
      })
      .addCase(getEligibilityCoursesResults.rejected, (state) => {
        state.loading = false;
        state.eligibilityCourses = [];
        state.courseTotal = 0;
      })
      .addCase(getEligibilityUniversity.pending, (state) => {
        state.eligibilityUniversities = [];
        state.universityTotal = 0;
      })
      .addCase(getEligibilityUniversity.fulfilled, (state, action) => {
        state.eligibilityUniversities = action.payload.list;
        state.universityTotal = action.payload.total;
      })
      .addCase(getEligibilityUniversity.rejected, (state) => {
        state.eligibilityUniversities = [];
        state.universityTotal = 0;
      })

      // GET GLOBAL SEARCH FILTER OPTIONS
      .addCase(getGlobalSearchFilter.pending, (state) => {
        state.courseFilterOptions = [];
        state.courseSpecialisationOptions = [];
        state.courseDuration = [];
        state.courseStream = [];
        state.specialisationCategory = [];
        state.intakeOptions = [];
        state.courseNames = [];
      })
      .addCase(getGlobalSearchFilter.fulfilled, (state, action) => {
        state.courseFilterOptions = action.payload?.course_levels;
        state.courseSpecialisationOptions =
          action.payload?.course_specialisations;
        state.courseDuration = action.payload?.durations;
        state.courseStream = action.payload?.course_streams;
        state.specialisationCategory = action.payload.college_specialisations;
        state.intakeOptions = action.payload?.course_intake_details;
        state.courseNames = action.payload?.course_names;
      })
      .addCase(getGlobalSearchFilter.rejected, (state) => {
        state.courseFilterOptions = [];
        state.courseSpecialisationOptions = [];
        state.courseDuration = [];
        state.courseStream = [];
        state.specialisationCategory = [];
        state.intakeOptions = [];
        state.courseNames = [];
      })

      .addCase(getFavoritesCards.pending, (state) => {
        state.loading = true;
      })
      .addCase(getFavoritesCards.fulfilled, (state, action) => {
        const payload = action.payload || {};

        state.favoritesCardList = {
          colleges: payload.colleges || [],
          courses: payload.courses || [],
        };

        state.loading = false;
      })
      .addCase(getFavoritesCards.rejected, (state, action) => {
        console.error("Failed to fetch favorites:", action.error);
        state.loading = false;
      })

      .addCase(getEligibilityDetails.pending, () => { })
      .addCase(getEligibilityDetails.fulfilled, (state, action) => {
        state.savedEligibility = {
          ...action.payload.data,
          countryId: action.payload.data?.id || action.payload.data?.countryId,
        };
      })
      .addCase(getEligibilityDetails.rejected, () => { })

      .addCase(saveEligibilityDetails.pending, () => { })
      .addCase(saveEligibilityDetails.fulfilled, (state, action) => {
        state.savedEligibility = {
          ...action.payload.data,
          countryId: action.payload.data?.id || action.payload.data?.countryId,
        };
      })
      .addCase(saveEligibilityDetails.rejected, () => { })

      .addCase("course/setCourseAcademicDetails", (state, action: any) => {
        const payload = action.payload || {};
        if (Object.keys(payload).length === 0) return;
        const academicDetails = ensureAcademicDetailsState(state);
        const englishProficiencyDetails =
          ensureEnglishProficiencyDetailsState(state);
        state.academicDetails = {
          ...initialState.academicDetails,
          ...academicDetails,
          ...payload,
        };
        if (payload.pastEnglishGrades !== undefined) {
          englishProficiencyDetails.englishMarks = payload.pastEnglishGrades;
        }
      })

      .addCase(
        "course/setCourseEnglishProficiencyDetails",
        (state, action: any) => {
          const payload = action.payload || {};
          if (Object.keys(payload).length === 0) return;
          const academicDetails = ensureAcademicDetailsState(state);
          const englishProficiencyDetails =
            ensureEnglishProficiencyDetailsState(state);
          state.englishProficiencyDetails = {
            ...initialState.englishProficiencyDetails,
            ...englishProficiencyDetails,
            ...payload,
          };
          if (payload.englishMarks !== undefined) {
            academicDetails.pastEnglishGrades = payload.englishMarks;
          }
        },
      )
      .addCase(submitCountryQuiz.pending, (state) => {
        if (!state.countryQuiz) {
          state.countryQuiz = { answers: [], result: null, loading: true };
        } else {
          state.countryQuiz.loading = true;
        }
      })
      .addCase(submitCountryQuiz.fulfilled, (state, action) => {
        if (!state.countryQuiz) {
          state.countryQuiz = { answers: [], result: action.payload, loading: false };
        } else {
          state.countryQuiz.result = action.payload;
          state.countryQuiz.loading = false;
        }
      })
      .addCase(submitCountryQuiz.rejected, (state) => {
        if (state.countryQuiz) {
          state.countryQuiz.loading = false;
        }
      });
  },
});

export const {
  setAiGlobalSearch,
  setCourseFinderFilters,
  setAiCourseFinderCountry,
  setAiCourseFinderAcademicDetails,
  setAiCourseFinderEnglishProficiencyDetails,
  setAiCourseFinderProgramDetails,
  setAiCourseFinderInstituteDetails,
  resetAiCourseFinderEnglishProficiencyDetails,
  resetAiCourseFinderAcademicDetails,
  resetAiCourseFinderInstituteDetails,
  resetAiCourseFinderProgramDetails,
  setCurrentDetail,
  clearAiCourseFinder,
  saveEligibilityAnonymous,
  setCountryQuizAnswer,
  setCountryQuizResult,
  setCountryQuizLoading,
  clearCountryQuiz,
  setGlobalSearchCourses,
  setUniversityComparisonData,
  setCourseComparisonData,
  setTopPickCard,
  setLastSearchTop3Courses,
  setGlobalSearchResults,
  setEligibilityUniversityResults,
  setEligibilityCoursesResults,
  restoreAiCourseFinderState,
} = aiCourseFinderSlice.actions;
export default aiCourseFinderSlice.reducer;
